import { z } from 'zod';

/**
 * Discount coupons. Staff create them in the admin (one at a time or many at once); customers type a code in the
 * cart. A coupon takes either a percentage off (optionally capped: "10% up to Rs 100") or a flat amount off, on orders
 * over a minimum, usable a limited number of times in total and by each customer. All money is in minor units (paise).
 */
export const COUPON_TYPES = ['percent', 'fixed', 'free_shipping'] as const;
export type CouponType = (typeof COUPON_TYPES)[number];

/** The most codes one "create many" request makes. */
export const COUPON_BULK_MAX = 200;

/** A code as customers type it: letters, numbers, dash and underscore; stored in capitals. */
export const COUPON_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,31}$/;
export const normalizeCouponCode = (raw: string) => raw.trim().toUpperCase().replace(/\s+/g, '');

const code = z
  .string()
  .transform(normalizeCouponCode)
  .refine((c) => COUPON_CODE_PATTERN.test(c), 'Use 3 to 32 letters, numbers, dashes or underscores');

const isoDate = z.string().datetime().nullable().default(null);
const limit = z.number().int().min(1, 'Must be at least 1').max(1_000_000).nullable().default(null);

const settings = z.object({
  description: z.string().trim().max(200).default(''),
  type: z.enum(COUPON_TYPES),
  /** percent (1-100), or the flat amount off in minor units; ignored for free shipping */
  value: z.number().int().min(0).default(0),
  /** for percent coupons, the most one order can save (minor units); null = no cap */
  maxDiscount: z.number().int().min(1, 'Must be more than 0').nullable().default(null),
  /** the order must come to at least this much (before tax and shipping) */
  minSubtotal: z.number().int().min(0).default(0),
  /** how many orders in total may use each code; null = unlimited */
  maxRedemptions: limit,
  /** how many times one customer may use each code; null = unlimited */
  perCustomerLimit: limit,
  startsAt: isoDate,
  endsAt: isoDate,
  isActive: z.boolean().default(true),
});

type SettingsInput = z.infer<typeof settings>;

/** Rules that depend on more than one field: shared by creating and (for the pieces it allows) editing. */
function checkSettings(v: Partial<SettingsInput>, ctx: z.RefinementCtx) {
  if (v.type === 'percent' && v.value !== undefined && (v.value < 1 || v.value > 100)) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'A percentage is between 1 and 100' });
  }
  if (v.type === 'fixed' && v.value !== undefined && v.value < 1) {
    ctx.addIssue({ code: 'custom', path: ['value'], message: 'Enter the amount off' });
  }
  if (v.startsAt && v.endsAt && new Date(v.startsAt) >= new Date(v.endsAt)) {
    ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'The end must be after the start' });
  }
}

export const couponCreateSchema = settings
  .extend({
    /** "single" makes the one code given; "bulk" makes `count` codes with random endings after the `prefix` */
    mode: z.enum(['single', 'bulk']),
    code: code.optional(),
    prefix: z
      .string()
      .transform(normalizeCouponCode)
      .refine((p) => /^[A-Z0-9]{2,12}$/.test(p), 'Use 2 to 12 letters or numbers')
      .optional(),
    count: z.number().int().min(2, 'Make at least 2').max(COUPON_BULK_MAX, `At most ${COUPON_BULK_MAX} at a time`).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'single' && !v.code) ctx.addIssue({ code: 'custom', path: ['code'], message: 'Enter the code' });
    if (v.mode === 'bulk' && !v.prefix) ctx.addIssue({ code: 'custom', path: ['prefix'], message: 'Enter a prefix for the codes' });
    if (v.mode === 'bulk' && !v.count) ctx.addIssue({ code: 'custom', path: ['count'], message: 'How many codes?' });
    checkSettings(v, ctx);
  })
  .transform((v) => ({ ...v, maxDiscount: v.type === 'percent' ? v.maxDiscount : null, value: v.type === 'free_shipping' ? 0 : v.value }));
export type CouponCreateInput = z.infer<typeof couponCreateSchema>;

/** What can be changed afterwards. The kind and value only while the coupon has not been used (the server checks). */
export const couponUpdateSchema = settings
  .partial()
  .superRefine((v, ctx) => {
    if (Object.keys(v).length === 0) ctx.addIssue({ code: 'custom', message: 'Nothing to change' });
    if (v.startsAt && v.endsAt && new Date(v.startsAt) >= new Date(v.endsAt)) {
      ctx.addIssue({ code: 'custom', path: ['endsAt'], message: 'The end must be after the start' });
    }
  });
export type CouponUpdateInput = z.infer<typeof couponUpdateSchema>;

/** The cart's "apply a code" box. Lenient: anything typed is tidied, the server decides whether it is a real code. */
export const applyCouponSchema = z.object({
  code: z.string().transform(normalizeCouponCode).refine((c) => c.length >= 1 && c.length <= 40, 'Enter a code'),
});

export type CouponStatus = 'active' | 'scheduled' | 'expired' | 'used_up' | 'disabled';

/** A coupon as staff see it. */
export interface AdminCouponDTO {
  id: string;
  code: string;
  description: string;
  type: CouponType;
  value: number;
  maxDiscount: number | null;
  minSubtotal: number;
  maxRedemptions: number | null;
  redemptions: number;
  perCustomerLimit: number | null;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  status: CouponStatus;
  batch: string | null;
  createdAt: string;
}
